// hooks/useVisualHelpers.js
import * as THREE from 'three';
import { useEffect, useCallback, useRef } from 'react';

// Visual Helpers Hook
const useVisualHelpers = () => {
  // Keep reference to the helpers group
  const helpersGroupRef = useRef(null);

  // Create text sprite for dimension labels
  const createTextSprite = useCallback((message) => {
    const fontFace = "Arial";
    const fontSize = 15;
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');

    // Increase resolution for better quality
    const scale = 4;
    canvas.width = 256 * scale;
    canvas.height = 128 * scale;
    context.scale(scale, scale);

    context.font = "bold " + fontSize + "px " + fontFace;

    // Get text width
    const metrics = context.measureText(message);
    const textWidth = metrics.width;

    // Draw background and text
    context.fillStyle = "rgba(255, 255, 255, 0.3)";
    context.fillRect(0, 0, textWidth + 20, fontSize + 20);
    context.fillStyle = "rgba(0, 0, 0, 1.0)";
    context.fillText(message, 10, fontSize + 10);

    // Create texture and sprite
    const texture = new THREE.CanvasTexture(canvas);
    texture.minFilter = THREE.LinearFilter;
    texture.generateMipmaps = false;
    const spriteMaterial = new THREE.SpriteMaterial({ map: texture, depthTest: false });
    const sprite = new THREE.Sprite(spriteMaterial);
    sprite.scale.set(textWidth / 2, fontSize + 10, 1);

    return sprite;
  }, []);

  // Create text labels for wires
  const createTextLabel = useCallback((text, position, angle = 0) => {
    const canvas = document.createElement("canvas");
    const context = canvas.getContext("2d");

    const fontSize = 18;
    const canvasWidth = 256;
    const canvasHeight = 128;

    canvas.width = canvasWidth;
    canvas.height = canvasHeight;

    // Move to center for rotation
    context.translate(canvasWidth / 2, canvasHeight / 2);
    context.rotate(angle);

    // Draw background
    context.fillStyle = "rgba(255, 255, 255, 0.8)";
    context.fillRect(-canvasWidth / 4, -fontSize, canvasWidth / 2, fontSize + 20);

    // Draw text
    context.font = `Bold ${fontSize}px Arial`;
    context.fillStyle = "rgba(0, 0, 0, 1)";
    const textWidth = context.measureText(text).width;
    context.fillText(text, -textWidth / 2, fontSize / 2);

    const texture = new THREE.CanvasTexture(canvas);
    const spriteMaterial = new THREE.SpriteMaterial({ map: texture });
    const sprite = new THREE.Sprite(spriteMaterial);
    sprite.position.copy(position);
    sprite.scale.set(30, 15, 1);

    return sprite;
  }, []);


  // Clean up function to remove helpers
  const removeVisualHelpers = useCallback((scene) => {
    if (!scene) return;

    // Find and remove the helpers group
    const helpersGroup = scene.getObjectByName('visualHelpers');
    if (helpersGroup) {
      // Remove the group from the scene
      scene.remove(helpersGroup);

      // Dispose of geometries and materials
      helpersGroup.traverse(object => {
        if (object.geometry) object.geometry.dispose();
        if (object.material) {
          if (Array.isArray(object.material)) {
            object.material.forEach(m => m.dispose());
          } else {
            object.material.dispose();
          }
        }
      });

      // Clear reference
      helpersGroupRef.current = null;
    }
  }, []);

  // Create visualization helpers
  const createVisualization = useCallback((object, scene, boundingBox) => {
    if (!scene || !object || !boundingBox) return null;

    // First remove any existing helpers
    removeVisualHelpers(scene);

    // Create a new group for helpers
    const helpersGroup = new THREE.Group();
    helpersGroup.name = 'visualHelpers';
    helpersGroupRef.current = helpersGroup;

    // Create bounding box helper
    const boxHelper = new THREE.BoxHelper(object);
    boxHelper.updateMatrixWorld(true);
    boxHelper.material.color.set(0x808080);
    helpersGroup.add(boxHelper);

    // Create corner markers (red cubes)
    const cubeGeometry = new THREE.BoxGeometry(2, 2, 2);
    const cubeMaterial = new THREE.MeshBasicMaterial({ color: 0xff0000, transparent: true, opacity: 0.5 });

    // Bounding box corners
    const corners = [
      new THREE.Vector3(boundingBox.min.x, boundingBox.min.y, boundingBox.min.z),
      new THREE.Vector3(boundingBox.min.x, boundingBox.min.y, boundingBox.max.z),
      new THREE.Vector3(boundingBox.min.x, boundingBox.max.y, boundingBox.min.z),
      new THREE.Vector3(boundingBox.min.x, boundingBox.max.y, boundingBox.max.z),
      new THREE.Vector3(boundingBox.max.x, boundingBox.min.y, boundingBox.min.z),
      new THREE.Vector3(boundingBox.max.x, boundingBox.min.y, boundingBox.max.z),
      new THREE.Vector3(boundingBox.max.x, boundingBox.max.y, boundingBox.min.z),
      new THREE.Vector3(boundingBox.max.x, boundingBox.max.y, boundingBox.max.z),
    ];

    // Transform cube positions based on object's world matrix
    corners.forEach(corner => {
      const cube = new THREE.Mesh(cubeGeometry, cubeMaterial);
      cube.position.copy(corner).applyMatrix4(object.matrixWorld); // Apply world transformation
     // helpersGroup.add(cube);
    });

    // Add labels
    const boxSize = boundingBox.getSize(new THREE.Vector3());

    const center = new THREE.Vector3();
    boundingBox.getCenter(center);

    const dimensionLabels = [
      {
        text: `Width: ${boxSize.x.toFixed(2)} mm`,
        position: new THREE.Vector3(center.x, boundingBox.min.y - 10, center.z)
      },
      {
        text: `Height: ${boxSize.y.toFixed(2)} mm`,
        position: new THREE.Vector3(boundingBox.max.x + 10, center.y, center.z)
      },
      {
        text: `Depth: ${boxSize.z.toFixed(2)} mm`,
        position: new THREE.Vector3(center.x, center.y, boundingBox.max.z + 10)
      }
    ];

    dimensionLabels.forEach(label => {
      const sprite = createTextSprite(label.text);

      // Apply world transformation to the label position
      label.position.applyMatrix4(object.matrixWorld);

      sprite.position.copy(label.position);
      helpersGroup.add(sprite);
    });

    // Add the helpers group to the scene
    scene.add(helpersGroup);

    return helpersGroup;
  }, [removeVisualHelpers, createTextSprite]);

  // Clean up when component unmounts
  useEffect(() => {
    return () => {
      // Clean up if the ref still exists when unmounting
      if (helpersGroupRef.current && helpersGroupRef.current.parent) {
        removeVisualHelpers(helpersGroupRef.current.parent);
      }
    };
  }, [removeVisualHelpers]);

  return {
    createTextSprite,
    createVisualization,
    removeVisualHelpers,
    createTextLabel
  };
};

export default useVisualHelpers;